/**
 * FreshCards Modal — generic dialog primitive.
 *
 * F 99.1: extracted from F 96 design. F 99.6: restructured + hardened.
 *
 * ── F 99.6 fix ────────────────────────────────────────────────────────────
 * v1.8.0 wrapped the panel INSIDE the backdrop, so a click that landed on
 * any panel child bubbled to the backdrop's own onClick only if the panel
 * failed to stop propagation — and the panel's stopPropagation then
 * swallowed genuine backdrop clicks too. Escape worked; backdrop click
 * did not. The fix is structural, not a patch: backdrop and panel are now
 * SIBLINGS. Only the backdrop carries onClick={onClose}. The panel
 * stops propagation on its own subtree. Matches Radix Dialog, Headless
 * UI Dialog, and Ariakit Dialog.
 *
 * ── Contract ──────────────────────────────────────────────────────────────
 *  - Escape closes (unless closeOnEscape={false})
 *  - Backdrop click closes (unless closeOnBackdropClick={false})
 *  - Panel clicks NEVER close
 *  - Portals to document.body
 *  - Focus trap: Tab/Shift+Tab cycle inside the panel
 *  - Focus returns to the opener on close
 *  - Body scroll locked while open, restored on close
 *  - role="dialog" + aria-modal="true" + aria-labelledby → the title node
 *  - Animates on mount; unmount is instant (no exit animation) so the
 *    focus-restore and scroll-unlock always land in the same tick
 *  - z-index is stacking-safe: backdrop = z, panel = z + 1
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Backdrop } from './Backdrop';

export interface ModalProps {
  /** Whether the modal is open. */
  opened: boolean;
  /** Close handler (called on Esc, backdrop click, X button). */
  onClose: () => void;
  /** Modal body content. */
  children: React.ReactNode;
  /** Optional header slot. */
  header?: React.ReactNode;
  /** Optional footer slot. */
  footer?: React.ReactNode;
  /**
   * Accessible name for the dialog. Rendered into a visually-hidden
   * node and referenced via aria-labelledby, so the dialog is named for
   * assistive tech even when `header` is a rich node rather than text.
   */
  title?: string;
  /** Max width (px or CSS string). Default 480. */
  maxWidth?: number | string;
  /** Inner padding (px). Default 16. */
  padding?: number;
  /** Border radius (px). Default 16. */
  borderRadius?: number;
  /**
   * Backdrop z-index. Panel renders at zIndex + 1.
   * Default 9999 (backdrop) / 10000 (panel).
   *
   * Why so high: app chrome already stacks above 1000 — the workspace
   * header is `position: sticky; z-index: 1100` and peek panels sit at
   * 1000. A modal at the old default of 100 was rendered *behind* the
   * header, so a backdrop click in the header band never reached the
   * backdrop and the modal appeared un-dismissable. Radix Dialog and
   * Headless UI both default dialogs to the top of the stack for the
   * same reason. Raise this only if the host has chrome above 9999.
   */
  zIndex?: number;
  /** aria-label for the close button. Default 'Close'. */
  closeLabel?: string;
  /** Whether clicking the backdrop closes the modal. Default true. */
  closeOnBackdropClick?: boolean;
  /** Whether pressing Escape closes the modal. Default true. */
  closeOnEscape?: boolean;
  /** Whether to trap focus inside the modal. Default true. */
  trapFocus?: boolean;
  /** Whether to lock body scroll while open. Default true. */
  lockScroll?: boolean;
  /** Optional className for the panel. */
  className?: string;
  /** Optional test id. */
  'data-testid'?: string;
  /** @deprecated Use `title` — it feeds aria-labelledby. Retained for back-compat. */
  ariaLabel?: string;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const Modal: React.FC<ModalProps> = ({
  opened,
  onClose,
  children,
  header,
  footer,
  title,
  maxWidth = 480,
  padding = 16,
  borderRadius = 16,
  zIndex = 9999,
  closeLabel = 'Close',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  trapFocus = true,
  lockScroll = true,
  className,
  'data-testid': dataTestId,
  ariaLabel,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const reactId = useId();
  const titleId = `fc-modal-title-${reactId}`;
  const dialogName = title ?? ariaLabel ?? 'Dialog';

  // Stable stacking root. Created imperatively and appended to <body> on
  // mount so <Backdrop> can portal into it on the same commit as the panel
  // (a ref would still be null when Backdrop first renders). Backdrop and
  // panel are siblings under this root — that is what makes
  // backdrop-click dismissal work.
  const [rootNode] = useState<HTMLElement | null>(() =>
    typeof document === 'undefined' ? null : document.createElement('div'),
  );

  useEffect(() => {
    // F 108 — only while OPEN. The root is appended unconditionally
    // before this guard, so every mounted-but-closed <Modal> (e.g. the
    // always-rendered QuickCaptureModal in WorkspaceSurface) left an
    // empty [data-fc-modal-root] div in <body> forever.
    if (!rootNode || !opened) return undefined;
    rootNode.setAttribute('data-fc-modal-root', '');
    // The root is the stacking context. It covers the viewport but must
    // NOT intercept pointer events itself — the backdrop and the panel are
    // its children and own all hit-testing. Without pointer-events:none the
    // root swallows every click and backdrop dismissal never fires.
    rootNode.style.position = 'fixed';
    rootNode.style.inset = '0';
    rootNode.style.zIndex = String(zIndex);
    rootNode.style.pointerEvents = 'none';
    document.body.appendChild(rootNode);
    return () => {
      if (rootNode.parentNode) rootNode.parentNode.removeChild(rootNode);
    };
  }, [rootNode, zIndex, opened]);

  // ── Focus: capture the opener on open, restore it on close ──
  useEffect(() => {
    if (!opened) return undefined;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // Focus the panel on the next frame — the portal is not in the DOM
    // until after this effect's commit.
    const raf = requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      // Restore focus to whatever opened the modal. Guarded: the opener
      // may have been unmounted while the modal was open.
      const el = openerRef.current;
      openerRef.current = null;
      if (el && typeof el.focus === 'function' && document.contains(el)) {
        el.focus();
      }
    };
  }, [opened]);

  // ── Escape ──
  useEffect(() => {
    if (!opened || !closeOnEscape) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [opened, closeOnEscape, onClose]);

  // ── Focus trap: Tab / Shift+Tab cycle within the panel ──
  useEffect(() => {
    if (!opened || !trapFocus) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;

    // No visibility filter: offsetParent / getClientRects are layout-
    // dependent and always report "hidden" in jsdom, which would empty
    // the list and silently disable the trap.
    const getFocusable = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.tabIndex !== -1 && !el.hasAttribute('disabled'),
      );

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        // Nothing focusable — keep focus on the panel itself.
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', onKey);
    return () => panel.removeEventListener('keydown', onKey);
  }, [opened, trapFocus]);

  // ── Body scroll lock ──
  useEffect(() => {
    if (!opened || !lockScroll) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    // Compensate for the removed scrollbar so the page does not shift.
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [opened, lockScroll]);

  if (!opened || typeof document === 'undefined' || !rootNode) return null;

  return createPortal(
    <>
      {/* ── SIBLING 1: backdrop. Sole owner of the dismiss-on-outside-click. */}
      <Backdrop
        opacity={0.45}
        zIndex={zIndex}
        container={rootNode}
        onClick={closeOnBackdropClick ? onClose : undefined}
      />

      {/* ── SIBLING 2: panel. Clicks here never dismiss. */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-fc-modal=""
        data-testid={dataTestId}
        className={className}
        onClick={(e) => e.stopPropagation()}
        style={{
          pointerEvents: 'auto',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
          width: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          backgroundColor: 'var(--fc-bg-surface, #ffffff)',
          borderRadius: `${borderRadius}px`,
          boxShadow: 'var(--fc-shadow-xl, 0 20px 50px rgba(0, 0, 0, 0.25))',
          zIndex: zIndex + 1,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
          animation: 'fc-modal-pop-in 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Accessible name. Visually hidden; referenced by aria-labelledby. */}
        <span id={titleId} data-fc-modal-title="" style={visuallyHidden}>
          {dialogName}
        </span>

        {header !== undefined || true ? (
          <div
            data-fc-modal-header=""
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `${padding}px`,
              borderBottom:
                header !== undefined ? '1px solid var(--fc-border-secondary, #e5e5e3)' : 'none',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              data-fc-modal-close=""
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                fontSize: 20,
                lineHeight: 1,
                color: 'var(--fc-text-secondary, #4b5563)',
                flexShrink: 0,
                marginLeft: 8,
              }}
            >
              ×
            </button>
          </div>
        ) : null}

        <div
          data-fc-modal-body=""
          style={{ padding: `${padding}px`, overflow: 'auto', flex: 1, minHeight: 0 }}
        >
          {children}
        </div>

        {footer !== undefined ? (
          <div
            data-fc-modal-footer=""
            style={{
              padding: `${padding}px`,
              borderTop: '1px solid var(--fc-border-secondary, #e5e5e3)',
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </>,
    rootNode,
  );
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default Modal;
